import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material'
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import { nomenclatorV2Api, SubregionPayload } from '../../services/nomenclatorV2Api'

interface Subregion {
  id: string
  parent_region_id: string
  display_name_en: string
  display_name_ro: string
  sort_order?: number
}

interface Region {
  id: string
  display_name_ro: string
}

export const SubregionsManager: React.FC = () => {
  const [subregions, setSubregions] = useState<Subregion[]>([])
  const [regions, setRegions] = useState<Region[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [subregionToDelete, setSubregionToDelete] = useState<string | null>(null)

  const [form, setForm] = useState<SubregionPayload>({
    id: '',
    parent_region_id: '',
    display_name_en: '',
    display_name_ro: '',
    sort_order: undefined
  })

  const loadSubregions = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await nomenclatorV2Api.listSubregions()
      setSubregions(data)
    } catch (err) {
      console.error('Failed to load subregions', err)
      setError('Nu am putut încărca subregiunile')
    } finally {
      setLoading(false)
    }
  }

  const loadRegions = async () => {
    try {
      const data = await nomenclatorV2Api.listRegions()
      setRegions(data)
    } catch (err) {
      console.error('Failed to load regions', err)
    }
  }

  useEffect(() => {
    loadSubregions()
    loadRegions()
  }, [])

  const handleSubmit = async () => {
    if (!form.id || !form.display_name_ro || !form.parent_region_id) {
      setError('ID, Nume RO și Regiune părinte sunt obligatorii')
      return
    }

    try {
      if (editingId) {
        await nomenclatorV2Api.updateSubregion(editingId, {
          parent_region_id: form.parent_region_id,
          display_name_en: form.display_name_en,
          display_name_ro: form.display_name_ro,
          sort_order: form.sort_order
        })
        setSuccess('Subregiune actualizată cu succes')
      } else {
        await nomenclatorV2Api.createSubregion({
          id: form.id,
          parent_region_id: form.parent_region_id,
          display_name_en: form.display_name_en,
          display_name_ro: form.display_name_ro,
          sort_order: form.sort_order
        })
        setSuccess('Subregiune adăugată cu succes')
      }
      resetForm()
      loadSubregions()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la salvare')
    }
  }

  const handleEdit = (subregion: Subregion) => {
    setForm({
      id: subregion.id,
      parent_region_id: subregion.parent_region_id,
      display_name_en: subregion.display_name_en,
      display_name_ro: subregion.display_name_ro,
      sort_order: subregion.sort_order
    })
    setEditingId(subregion.id)
  }

  const handleDeleteClick = (id: string) => {
    setSubregionToDelete(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!subregionToDelete) return

    try {
      await nomenclatorV2Api.deleteSubregion(subregionToDelete)
      setSuccess('Subregiune ștearsă cu succes')
      loadSubregions()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la ștergere')
    } finally {
      setDeleteDialogOpen(false)
      setSubregionToDelete(null)
    }
  }

  const resetForm = () => {
    setForm({
      id: '',
      parent_region_id: '',
      display_name_en: '',
      display_name_ro: '',
      sort_order: undefined
    })
    setEditingId(null)
  }

  const getRegionName = (regionId: string) => {
    return regions.find((r) => r.id === regionId)?.display_name_ro || regionId
  }

  if (loading && subregions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  // Compact input styles
  const inputSx = {
    '& .MuiInputBase-root': { fontSize: '0.8rem', height: 32 },
    '& .MuiInputLabel-root': { fontSize: '0.75rem' }
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0.5, fontSize: '0.78rem' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess(null)} message={success} />

      {/* Form - Compact */}
      <Card sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <AddIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {editingId ? 'Editează Subregiune' : 'Adaugă Subregiune Nouă'}
            </Typography>
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <TextField
              label="Cod (EN)"
              value={form.id}
              onChange={(e) => setForm({ ...form, id: e.target.value })}
              disabled={!!editingId}
              required
              size="small"
              sx={{ width: 140, ...inputSx }}
            />
            <FormControl required size="small" sx={{ minWidth: 150, ...inputSx }}>
              <InputLabel>Regiune Părinte</InputLabel>
              <Select
                value={form.parent_region_id}
                onChange={(e) => setForm({ ...form, parent_region_id: e.target.value })}
                label="Regiune Părinte"
              >
                {regions.map((region) => (
                  <MenuItem key={region.id} value={region.id} sx={{ fontSize: '0.8rem' }}>
                    {region.display_name_ro}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Nume RO"
              value={form.display_name_ro}
              onChange={(e) => setForm({ ...form, display_name_ro: e.target.value })}
              required
              size="small"
              sx={{ flex: 1, minWidth: 150, ...inputSx }}
            />
            <TextField
              label="Nume EN"
              value={form.display_name_en}
              onChange={(e) => setForm({ ...form, display_name_en: e.target.value })}
              size="small"
              sx={{ flex: 1, minWidth: 150, ...inputSx }}
            />
            <TextField
              label="Ordine"
              type="number"
              value={form.sort_order ?? ''}
              onChange={(e) => setForm({ ...form, sort_order: e.target.value ? Number(e.target.value) : undefined })}
              size="small"
              sx={{ width: 80, ...inputSx }}
            />
            <Button 
              variant="contained" 
              onClick={handleSubmit} 
              disabled={!form.id || !form.display_name_ro || !form.parent_region_id}
              size="small"
              sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}
            >
              {editingId ? 'Salvează' : 'Adaugă'}
            </Button>
            {editingId && (
              <Button variant="outlined" onClick={resetForm} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>
                Anulează
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Table - Compact */}
      <Card sx={{
        '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' },
        '& .MuiTableBody-root .MuiIconButton-root': { p: 0.25 },
        '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
      }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
            Subregiuni ({subregions.length})
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow sx={{
                bgcolor: 'grey.900',
                '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.5, px: 1 }
              }}>
                <TableCell>Cod</TableCell>
                <TableCell>Regiune</TableCell>
                <TableCell>Nume RO</TableCell>
                <TableCell>Nume EN</TableCell>
                <TableCell align="right">Ordine</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subregions.map((subregion) => (
                <TableRow key={subregion.id} hover>
                  <TableCell><strong>{subregion.id}</strong></TableCell>
                  <TableCell>{getRegionName(subregion.parent_region_id)}</TableCell>
                  <TableCell>{subregion.display_name_ro}</TableCell>
                  <TableCell>{subregion.display_name_en}</TableCell>
                  <TableCell align="right">{subregion.sort_order ?? '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(subregion)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(subregion.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Dialog - Compact */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}>
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
          Confirmare ștergere
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '0.85rem' }}>Sigur vrei să ștergi subregiunea <strong>{subregionToDelete}</strong>?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small" sx={{ fontSize: '0.8rem' }}>Anulează</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" size="small" sx={{ fontSize: '0.8rem' }}>Șterge</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

